import { useEffect, useState } from 'react'
import useStateRef from 'react-usestateref'
import dottie from 'dottie'
import { validate as validateSchema } from 'jsonschema'
import clone from 'just-clone'
import deepEqual from 'fast-deep-equal'

export function useSchema(schema, initialValues, options) {
  const [formState, setFormState, formStateRef] = useStateRef(initialValues || {})
  useEffect(() => setFormState(initialValues || {}), [initialValues])
  const [dirtyPaths, setDirtyPaths] = useState(new Set())
  const [unValidatedPaths, setUnValidatedPaths] = useState(new Set())
  const [errorCache, setErrorCache] = useState(new Map())

  /* const ajv = useMemo(() => {
    const ajvInstance = new Ajv({ format: 'full', coerceTypes: true, allErrors: true, jsonPointers: true, $data: true, ...(options?.ajvInit) })
    require('ajv-errors')(ajvInstance)
    return ajvInstance
  }, [options?.ajvInit])
  const ajvValidation = useMemo(() => ajv.compile(schema), [schema]) */
  // console.log('ajvValidation', ajvValidation, ajvValidation.errors, ajvValidation.schema)

  const getSubPath = (root, suffix) => root ? `${root}.${suffix}` : String(suffix)

  const getSubSchema = function(path) {
    if (!Array.isArray(path)) {
      path = String(path)
      path = path.split('.')
    }
    let subSchema = schema
    for (const pathPart of path) {
      if (pathPart == null || pathPart === '' || !subSchema) continue
      else if (subSchema.items && !isNaN(pathPart)) subSchema = subSchema.items
      else if (subSchema.properties) subSchema = subSchema.properties[pathPart]
      // if (!subSchema) return null
      else return null
    }
    return subSchema
  }

  /* const forEachParent = function(path, callback, { reverse = false } = {}) {
    if (!Array.isArray(path)) {
      path = String(path)
      path = path.split('.')
    }
    const paths = path.reduce((arr, part, i) => {
      if (i === 0) arr.push([part])
      arr.push(arr[arr.length - 1].concat(part))
      return arr
    }, [])
    if (reverse) paths.reverse()
    for (let i = 0; i < paths.length; i++) {
      // this feels like it could be optimized, but it would be different for reverse mode
      const value = get(paths[i])
      const schema = getSubSchema(paths[i])
      callback(value, schema)
    }
  } */

  const forEachNode = function(callback, start = {}) {
    const dataNode = 'dataNode' in start ? start.dataNode : formStateRef.current
    const schemaNode = 'schemaNode' in start ? start.schemaNode : schema
    const path = 'path' in start ? start.path : ''
    forEachChild(callback, { dataNode, schemaNode, path })
  }
  const forEachChild = function(callback, { dataNode, schemaNode, path }) {
    callback(dataNode, schemaNode, path)
    if (Array.isArray(dataNode)) {
      dataNode.forEach((child, i) => {
        const subSchema = schemaNode?.items
        const subPath = getSubPath(path, i)
        forEachChild(callback, { dataNode: child, schemaNode: subSchema, path: subPath })
      })
    } else if (typeof dataNode === 'object' && dataNode != null) {
      Object.entries(dataNode).forEach(([key, child]) => {
        const subSchema = schemaNode?.properties?.[key]
        const subPath = getSubPath(path, key)
        forEachChild(callback, { dataNode: child, schemaNode: subSchema, path: subPath })
      })
    }
  }

  // decided on children first since the schema may not apply after transforming the parent
  const mapEachNode = function(callback, start = {}) {
    const dataNode = 'dataNode' in start ? start.dataNode : formStateRef.current
    const schemaNode = 'schemaNode' in start ? start.schemaNode : schema
    const path = 'path' in start ? start.path : ''
    let childResult = dataNode
    if (childResult == null) {
      // do nothing
    } else if (schemaNode?.items && Array.isArray(childResult)) {
      childResult = childResult.map((item, idx) => {
        const subPath = getSubPath(path, idx)
        const subSchema = getSubSchema(subPath)
        return mapEachNode(callback, { dataNode: item, schemaNode: subSchema, path: subPath })
      })
    } else if (schemaNode?.properties && typeof childResult === 'object') {
      childResult = Object.keys(childResult).reduce((obj, key) => {
        const subPath = getSubPath(path, key)
        const subSchema = getSubSchema(subPath)
        obj[key] = mapEachNode(callback, { dataNode: childResult[key], schemaNode: subSchema, path: subPath })
        return obj
      }, {})
    }
    return callback(childResult, { beforeMapping: dataNode, schema: schemaNode, absolutePath: path })
  }

  // TODO optimize by not using dottie?
  const allPresentPaths = function(object) {
    const leaves = dottie.paths(object)
    return [...leaves.reduce((set, leaf) => {
      let idx = leaf.length
      do {
        leaf = leaf.substring(0, idx)
        set.add(leaf)
        idx = leaf.lastIndexOf('.')
      } while (idx >= 0)
      return set
    }, new Set())]
  }

  const get = function(path) {
    // TODO
    if (path) return dottie.get(formStateRef.current, path)
    else return formStateRef.current
  }

  const getCurrentSchemaValue = function(property, path) {
    if (!Array.isArray(path)) {
      path = String(path)
      path = path.split('.')
    }
    const paths = path.reduce((arr, part, i) => {
      if (i === 0) arr.push([part])
      arr.push(arr[arr.length - 1].concat(part))
      return arr
    }, [])
    for (let i = paths.length - 1; i >= 0; i--) {
      const subSchema = getSubSchema(paths[i]) ?? {}
      if (property in subSchema) return subSchema[property]
    }
    return schema[property]
  }

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const url = reader.result
        resolve(url.slice(url.lastIndexOf(',') + 1))
      }
      reader.onerror = event => reject(event)
      reader.readAsDataURL(blob)
    })
  }

  const onChange = async function(change) {
    const target = change.currentTarget || change.target
    // console.log('onChange', target, target?.name, target?.value, target?.checked, target?.type, target?.files)
    if (target?.value && target?.type === 'checkbox' && target.value !== 'on') {
      change = { [target.value]: target.checked }
    } else if (target?.name && target?.type === 'checkbox') {
      change = { [target.name]: target.checked }
    } else if (target?.name && target?.type === 'file' && !target.files?.[0]) {
      change = { [target.name]: null }
    } else if (target?.name && target?.type === 'file') {
      const file = target.files[0]
      change = { [target.name]: { fileObject: file, fileName: file.name, fileContent: await blobToBase64(file) } }
      // console.log('onChange file', file, change)
    } else if (target?.name && target?.value !== undefined) {
      change = { [target.name]: target.value }
    }
    // console.log('starting onChange', change)
    if (options?.mutateOnChange) {
      for (const [path, value] of Object.entries(change)) {
        dottie.set(formState, path, value)
      }
      setFormState(formState)
    } else {
      const allPaths = [...Object.keys(change).reduce((set, path) => {
        const parts = String(path).split('.')
        let pth = null
        for (const part of parts) {
          pth = (pth == null ? '' : pth + '.') + part
          set.add(pth)
        }
        return set
      }, new Set())].sort((a, b) => a.length - b.length)
      // console.log('allPaths', allPaths)
      const newValue = { ...formStateRef.current }
      for (const path of allPaths) {
        if (path in change) {
          dottie.set(newValue, path, change[path])
          dirtyPaths.add(path)
          unValidatedPaths.add(path)
        } else {
          const toShallow = dottie.get(newValue, path)
          if (Array.isArray(toShallow)) dottie.set(newValue, path, [...toShallow])
          else dottie.set(newValue, path, { ...toShallow })
        }
      }
      // console.log('ending onChange', newValue)
      setFormState(newValue)
    }
    return change
  }

  const sanitize = function(start = {}) {
    const dataNode = 'dataNode' in start ? start.dataNode : formStateRef.current
    const schemaNode = 'schemaNode' in start ? start.schemaNode : schema
    const path = 'path' in start ? start.path : ''
    return mapEachNode((childValue, { beforeMapping, schema, absolutePath }) => {
      const typeArray = Array.isArray(schema?.type) ? schema.type : (schema?.type ? [schema.type] : [])
      let value = childValue
      if (typeof value === 'string') value = value.trim()
      if (typeof value === 'string' && value === '' && !schema?.allowEmptyStrings) value = null
      else if (typeof value === 'string' && !typeArray.includes('string') && !isNaN(value) && typeArray.some(x => x === 'number' || x === 'integer')) value = Number(value)
      else if (typeof value === 'string' && !typeArray.includes('string') && (value === 'true' || value === 'false')) value = (value === 'true')
      else if (Array.isArray(value)) {
        if (value.length === 0 && !schema?.allowEmptyArrays) value = null
        else if (value != null) value = clone(value)
      } else if (typeof value === 'object' && schema?.readFormat && value?.fileContent != null) value = value.fileContent
      else if (typeof value === 'object' && value !== null && Object.keys(value).length === 0 && !schema?.allowEmptyObjects) value = null
      else if (typeof value === 'object' && value != null) value = clone(value)
      if (value === null && getCurrentSchemaValue('removeNulls', absolutePath)) value = undefined
      if (typeof schema?.onSanitize === 'function') value = schema.onSanitize(value, { path, schema, state: get() })
      // console.log('@sanitize', absolutePath, childValue, beforeMapping, value, schema)
      return value
    }, { dataNode, schemaNode, path })
  }

  const modifyAjvErrors = (
    ('modifyAjvErrors' in (options || {}))
      ? options.modifyAjvErrors
      : function(err, path) {
        if (path && Array.isArray(err.path)) err.path = [path, ...err.path]
        if (err.name === 'required') err.path = [...err.path, err.argument]
      }
  )

  const validate = function(paths) {
    if (!paths) paths = [undefined]
    if (!Array.isArray(paths)) paths = [paths]
    const allErrors = []
    for (const pth of paths) {
      const pathErrors = []
      clearErrors(pth)
      clearUnvalidated(pth)
      let dataNode = pth ? get(pth) : formStateRef.current
      const schemaNode = pth ? getSubSchema(pth) : schema
      // console.log('started validate', pth, dataNode, schemaNode)
      dataNode = sanitize({ dataNode, schemaNode, pth })
      if (!schemaNode) continue
      const jsonResult = validateSchema(dataNode, schemaNode)
      // console.log('validated schema', dataNode, schemaNode, JSON.stringify(jsonResult))
      if (Array.isArray(jsonResult?.errors)) pathErrors.push(...jsonResult.errors)
      if (modifyAjvErrors) {
        for (const err of pathErrors) {
          modifyAjvErrors(err, pth)
        }
      }
      forEachNode((node, schema, path) => {
        if (schema && typeof schema.onValidate === 'function') {
          // console.log('in validate forEachNode', path, node, schema)
          try {
            const validation = schema.onValidate(node, { path, schema, state: get() })
            const getError = (err) => {
              const schemaPath = `instance.${path}`.split('.')
              // console.log('in onValidate', path, schemaPath, err)
              if (typeof err === 'object') return { path: schemaPath.slice(1), property: schemaPath.join('.'), argument: schemaPath[schemaPath.length - 1], instance: node, name: 'custom', ...err }
              else return { path: schemaPath.slice(1), property: schemaPath.join('.'), argument: schemaPath[schemaPath.length - 1], instance: node, name: 'custom', message: err }
            }
            // console.log('ran onValidate', path, JSON.stringify(validation))
            if (Array.isArray(validation)) {
              pathErrors.push(...validation.map(err => {
                return getError(err)
              }))
            } else if (validation) pathErrors.push(getError(validation))
          } catch (err) {
            pathErrors.push({ path, ...err })
          }
        }
      }, { dataNode, schemaNode, path: pth })
      allErrors.push(...pathErrors)
    }
    for (const err of allErrors) {
      const subSchema = getSubSchema(err?.path)
      const schemaError = subSchema?.errorMessage
      const parseSchemaError = (schemaErr) => {
        if (schemaErr == null || typeof schemaErr === 'string') return schemaErr
        else if (typeof schemaErr === 'function') return schemaErr(err)
        else if (schemaErr?.[err?.name]) return parseSchemaError(schemaErr?.[err?.name])
        else return schemaErr
      }
      const errorResult = parseSchemaError(schemaError)
      if (errorResult) {
        if (typeof errorResult === 'string') err.message = errorResult
        else Object.assign(err, errorResult)
      }
      const schemaErrorType = subSchema?.errorType
      if (!err.type && schemaErrorType && typeof schemaErrorType === 'string') err.type = schemaErrorType
      else if (!err.type && schemaErrorType && typeof schemaErrorType === 'object' && err.name in schemaErrorType) err.type = schemaErrorType[err.name]
    }
    // console.log('after validate', path, schema, allErrors)
    if (Object.keys(allErrors).length > 0) {
      addErrors(allErrors)
      return allErrors
    } else {
      return null
    }
  }

  const getError = function(path) {
    if (Array.isArray(path)) path = path.join('.')
    return errorCache.get(path)
  }
  const addErrors = function(errors) {
    if (!Array.isArray(errors)) return
    // console.log('addErrors', errors, errorCache)
    for (const err of errors) {
      const path = Array.isArray(err.path) ? err.path.join('.') : err.path
      if (errorCache.has(path)) {
        errorCache.set(path, [].concat(errorCache.get(path)).concat(err))
      } else errorCache.set(path, err)
    }
    setErrorCache(new Map(errorCache))
  }
  const clearErrors = function(path) {
    if (path) {
      for (const k of errorCache.keys()) {
        // console.log('clearErrors', path, k)
        if (k.startsWith(path)) {
          errorCache.delete(k)
        }
      }
    } else {
      // console.log('clearing whole errorCache')
      errorCache.clear()
    }
    setErrorCache(new Map(errorCache))
  }
  const getChildErrors = function(paths) {
    if (!paths) return [...errorCache.values()]
    if (!Array.isArray(paths)) paths = [paths]
    const matchingErrors = []
    for (const pathKey of errorCache.keys()) {
      if (paths.some(p => pathKey.startsWith(p))) matchingErrors.push(errorCache.get(pathKey))
    }
    return matchingErrors
  }
  const hasChildError = function(paths) {
    if (!paths) return Boolean(errorCache.size)
    if (!Array.isArray(paths)) paths = [paths]
    for (const pathKey of errorCache.keys()) {
      if (paths.some(p => pathKey.startsWith(p))) return true
    }
    return false
  }

  const reset = function(options) {
    if (options && 'newValue' in options) setFormState(options.newValue)
    else setFormState(initialValues)
    setDirtyPaths(new Set())
    setUnValidatedPaths(new Set())
    clearErrors()
  }

  const resetToCurrent = function() {
    setDirtyPaths(new Set())
    setUnValidatedPaths(new Set())
    clearErrors()
  }

  // Remove readOnly properties from an object. Mutates the object.
  const stripReadOnly = function(state, rootPath = '', removeIdFields = false) {
    if (typeof state !== 'object' || !state) return state
    for (const path of allPresentPaths(state)) {
      const subSchema = getSubSchema(rootPath ? (`${rootPath}.${path}`) : path)
      if (subSchema?.readOnly || (removeIdFields && subSchema?.idField)) {
        const lastDot = path.lastIndexOf('.')
        const parent = lastDot < 0 ? state : dottie.get(state, path.substring(0, lastDot))
        delete parent[lastDot < 0 ? path : path.substring(lastDot + 1)]
      }
    }
    return state
  }

  const getPost = function() {
    // const state = clone(formState)
    const state = sanitize()
    return stripReadOnly(state, undefined, true)
  }

  const getPut = function() {
    // const state = clone(formState)
    const state = sanitize()
    return stripReadOnly(state)
  }

  const getPatch = function() {
    if (deepEqual(formStateRef.current, initialValues)) return {}
    const state = sanitize()
    const changes = {}
    const arrayPaths = []
    const paths = []
    // console.log('before patch', state, initialValues)
    do {
      const queuedItem = paths.shift()
      const path = queuedItem?.path ?? queuedItem
      const node = queuedItem == null ? state : (queuedItem?.node ?? dottie.get(state, path))
      const init = clone(queuedItem == null ? initialValues : (queuedItem?.init ?? dottie.get(initialValues, path)))
      // console.log('@patchLoop', queuedItem, path, changes, node, init)
      // console.log('patchLoop will compare', path, stripReadOnly(node, path), stripReadOnly(init, path))
      if (deepEqual(stripReadOnly(node, path), stripReadOnly(init, path))) continue
      const subSchema = path == null ? schema : getSubSchema(path)
      if (subSchema?.readOnly) continue
      // console.log('@doSomething', path, node, init, subSchema)
      if (Array.isArray(node) && Array.isArray(init)) {
        const replacementInfo = { path }
        if (subSchema?.patchMethod === 'diff') {
          paths.push(...node.map((x, i) => getSubPath(path, i)))
          for (let i = node.length; i < init.length; i++) {
            changes[`${path}.${i}`] = null
          }
        } else if (subSchema?.patchMethod === 'key') {
          const idField = subSchema?.items?.properties == null ? undefined : Object.keys(subSchema.items.properties)?.find(prop => subSchema.items.properties[prop]?.idField)
          if (!idField) throw new Error('The "key" patchMethod requires that items of the array have a property marked with "idField: true".')
          // console.log('idField', idField, path, node, init, subSchema)
          if (!node.length) changes[path] = []

          replacementInfo.keyMap = {}
          for (let i = 0; i < node.length; i++) {
            const newItem = node[i]
            const oldItem = init.find(item => item[idField] === newItem[idField])

            // console.log('old/new', oldItem, newItem)
            replacementInfo.keyMap[i] = { [idField]: newItem[idField] }
            if (!oldItem) {
              changes[`${path}.${i}`] = newItem
            } else {
              changes[`${path}.${i}.${idField}`] = newItem[idField]
              paths.push({ path: getSubPath(path, i), init: oldItem })
              // console.log('@key pushed', { path: getSubPath(path, i), init: oldItem })
            }
          }
        } else { // default for arrays is 'replace' mode
          changes[path] = clone(node)
        }
        arrayPaths.push(replacementInfo)
      } else if (!Array.isArray(node) && !Array.isArray(init) && node != null && init != null && typeof node === 'object' && typeof init === 'object') {
        if (subSchema?.patchMethod === 'replace') {
          changes[path] = clone(node)
        } else { // default for objects is 'diff' mode
          paths.push(...Object.keys(node).map(key => ({ path: getSubPath(path, key), node: node[key], init: init[key] })))
          // console.log('@obj pushed', [...Object.keys(node).map(key => getSubPath(path, key))])
          for (const key of Object.keys(init)) {
            if (!(key in node)) changes[getSubPath(path, key)] = null
          }
        }
      } else {
        changes[path] = typeof node === 'object' && node != null ? clone(node) : node
      }
    } while (paths.length)
    // console.log('post patch loop', changes)
    const objResult = dottie.transform(changes)
    // console.log('after main loop', JSON.stringify(objResult), arrayPaths)
    for (const { path, keyMap } of arrayPaths) {
      const subObj = dottie.get(objResult, path)
      if (subObj && !Array.isArray(subObj)) {
        if (keyMap) {
          dottie.set(objResult, path, Object.entries(subObj).map(([idx, subSubObj]) => ({ ...subSubObj, ...keyMap[idx] })))
        } else {
          const maxIndex = Object.keys(subObj).reduce((max, idx) => Math.max(Number(idx), max), -1)
          const newArr = Array(maxIndex + 1)
          for (let i = 0; i < maxIndex; i++) {
            if (i in subObj) newArr[i] = subObj[i]
            else newArr[i] = {}
          }
          dottie.set(objResult, path, newArr)
        }
      }
    }
    return stripReadOnly(objResult)
  }

  const transform = function(value) {
    return mapEachNode((val, context) => {
      if (typeof context?.schema?.onTransform === 'function') {
        return context.schema.onTransform(val, context, { state: get() })
      } else {
        return val
      }
    }, { dataNode: value })
  }

  const isDirty = function(key) {
    if (key) return dirtyPaths.has(key)
    else return dirtyPaths.size > 0
  }

  const isUnValidated = function(key) {
    if (key) return unValidatedPaths.has(key)
    else return unValidatedPaths.size > 0
  }
  const clearUnvalidated = function(path) {
    if (path) {
      for (const k of unValidatedPaths) {
        if (k.startsWith(path)) {
          unValidatedPaths.delete(k)
        }
      }
    } else {
      // console.log('clearing whole errorCache')
      unValidatedPaths.clear()
    }
    setUnValidatedPaths(new Set(unValidatedPaths))
  }

  return { get, onChange, validate, reset, resetToCurrent, getPost, getPut, getPatch, sanitize, transform, isDirty, isUnValidated, getError, clearErrors, getChildErrors, hasChildError, getCurrentSchemaValue }
}

// error types
// // make sure it works for custom errors
// // figure out how to set the type in schema and have it show up in the error
// does error message templating work on front end?
// list of paths as parameter to validate()
// how to embed constants in a schema?
// add sanitize before validate
